<template>
  <div
    id="bulk-handling-fee-modal"
    ref="bulkHandlingFeeModal"
    class="modal fade"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
    data-backdrop="static"
    data-keyboard="false"
  >
    <div class="modal-dialog" role="document" style="max-width: 600px;">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h5 class="modal-title">Bulk Update Handling Fee</h5>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Bulk Update Info Alert -->
          <div class="alert alert-info mb-3">
            <div class="row">
              <div class="col-md-12">
                <i class="material-icons md-sm" style="vertical-align: middle;">info</i>
                Updating handling fee for <strong>{{ selectedCount }}</strong> selected product(s)
              </div>
            </div>
          </div>

          <form @submit.stop.prevent="handleSubmit">
            <!-- Fee Configuration -->
            <div class="form-group">
              <label>Calculation Model</label>
              <div class="row">
                <div class="col-md-6">
                  <!-- Fix per Order -->
                  <button
                    type="button"
                    class="btn p-3 text-left w-100 border text-secondary handling-fee-btn"
                    :class="{
                      'active-warning': form.type === 'per_order'
                    }"
                    @click="form.type = 'per_order'"
                  >
                    <div class="font-weight-bold">
                      Fix per Order
                    </div>
                    <div class="text-muted" style="font-size: 11px; margin-top: 4px; line-height: 1.2;">
                      Biaya flat 1x per SKU dalam satu transaksi.
                    </div>
                  </button>
                </div>
                <div class="col-md-6">
                  <!-- Multiple per Qty -->
                  <button
                    type="button"
                    class="btn p-3 text-left w-100 border text-secondary handling-fee-btn"
                    :class="{
                      'active-warning': form.type === 'per_qty'
                    }"
                    @click="form.type = 'per_qty'"
                  >
                    <div class="font-weight-bold">
                      Multiple per Qty
                    </div>
                    <div class="text-muted" style="font-size: 11px; margin-top: 4px; line-height: 1.2;">
                      Biaya dikalikan dengan kuantitas item.
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <!-- Fee Values -->
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Fee Supplier</label>
                  <div class="input-group d-flex align-items-center">
                    <div class="input-group-prepend">
                      <div class="input-group-text">Rp</div>
                    </div>
                    <input
                      v-model.number="form.fee_supplier"
                      type="number"
                      step="1"
                      min="0"
                      class="form-control"
                      placeholder="Masukkan Fee Supplier"
                      @input="calculateTotal"
                    />
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Margin Platform</label>
                  <div class="input-group d-flex align-items-center">
                    <div class="input-group-prepend">
                      <div class="input-group-text">Rp</div>
                    </div>
                    <input
                      v-model.number="form.margin_platform"
                      type="number"
                      step="1"
                      min="0"
                      class="form-control"
                      placeholder="Masukkan Margin Platform"
                      @input="calculateTotal"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Handling Fee to Seller -->
            <div class="alert alert-info mb-0" style="background-color: #e3f2fd; border-color: #2196f3;">
              <div class="d-flex">
                <i class="material-icons mr-2" style="color: #1976d2;">info</i>
                <div class="flex-grow-1">
                  <strong class="text-primary">Total Handling Fee (to Seller)</strong>
                  <div class="mt-1">
                    <span class="font-weight-bold" style="font-size: 16px;">Rp {{ formatRupiah(form.value) }}</span>
                    <span v-if="form.type === 'per_order'"> per order</span>
                    <span v-else> per product</span>
                  </div>
                  <small class="text-muted d-block mt-1">
                    Seller hanya akan melihat total gabungan ini. Pembagian fee supplier bersifat internal sistem.
                  </small>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="!isValid || submitting"
          >
            <span v-if="submitting">
              <i class="fa fa-spinner fa-spin"></i> Updating...
            </span>
            <span v-else>
              <i class="fa fa-save"></i> Update {{ selectedCount }} Product(s)
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { bulkUpdateHandlingFee } from '@/api/admin';

export default {
	name: 'BulkHandlingFeeModal',
	props: {
		selectedProducts: {
			type: Array,
			required: true,
			default: () => [],
		},
	},
	data() {
		return {
			form: {
				type: 'per_order',
				fee_supplier: 0,
				margin_platform: 0,
				value: 0,
			},
			submitting: false,
		};
	},
	computed: {
		selectedCount() {
			return this.selectedProducts.length;
		},
		isValid() {
			return (
				this.form.fee_supplier >= 0
        && this.form.margin_platform >= 0
        && (this.form.fee_supplier > 0 || this.form.margin_platform > 0)
			);
		},
	},
	methods: {
		resetForm() {
			this.form = {
				type: 'per_order',
				fee_supplier: 0,
				margin_platform: 0,
				value: 0,
			};
			this.submitting = false;
		},
		calculateTotal() {
			// Ensure values are numbers
			const feeSupplier = parseFloat(this.form.fee_supplier) || 0;
			const marginPlatform = parseFloat(this.form.margin_platform) || 0;
			this.form.value = feeSupplier + marginPlatform;
		},
		formatRupiah(value) {
			// Format number with thousand separator
			return new Intl.NumberFormat('id-ID').format(value || 0);
		},
		show() {
			// Use jQuery to show modal if available, otherwise use native
			if (window.$ && this.$refs.bulkHandlingFeeModal) {
				window.$(this.$refs.bulkHandlingFeeModal).modal('show');
			}
		},
		hide() {
			// Use jQuery to hide modal if available, otherwise use native
			if (window.$ && this.$refs.bulkHandlingFeeModal) {
				window.$(this.$refs.bulkHandlingFeeModal).modal('hide');
			}
		},
		handleHidden() {
			this.resetForm();
			this.$emit('hidden');
		},
		handleCancel() {
			this.hide();
		},
		handleSubmit() {
			if (!this.isValid) {
				this.$notify({
					group: 'products',
					title: 'Validation Error',
					text: 'Please fill in valid fee amounts',
					type: 'error',
				});
				return;
			}

			this.submitting = true;

			const handlingFeeConfig = {
				type: this.form.type,
				fee_supplier: this.form.fee_supplier,
				margin_platform: this.form.margin_platform,
				value: this.form.value,
			};

			const callback = (response) => {
				const message = response?.message ?? '';
				const updatedCount = response?.data?.updated_count ?? 0;
				const failedCount = response?.data?.failed_count ?? 0;

				this.$notify({
					group: 'products',
					title: 'Bulk Update Handling Fee',
					text: message || `Successfully updated ${updatedCount} products`,
					type: 'success',
				});

				if (failedCount > 0) {
					this.$notify({
						group: 'products',
						title: 'Warning',
						text: `${failedCount} products failed to update`,
						type: 'warn',
					});
				}

				this.submitting = false;
				this.hide();

				// Emit event to parent to refresh product list
				this.$emit('success');
			};

			const errorCallback = () => {
				this.$notify({
					group: 'products',
					title: 'Bulk Update Handling Fee',
					text: 'Failed to update handling fee',
					type: 'error',
				});

				this.submitting = false;
			};

			bulkUpdateHandlingFee(this.selectedProducts, handlingFeeConfig, callback, errorCallback);
		},
	},
	mounted() {
		// Add event listener for when modal is hidden
		if (window.$ && this.$refs.bulkHandlingFeeModal) {
			window.$(this.$refs.bulkHandlingFeeModal).on('hidden.bs.modal', () => {
				this.handleHidden();
			});
		}
	},
	beforeDestroy() {
		// Clean up event listener
		if (window.$ && this.$refs.bulkHandlingFeeModal) {
			window.$(this.$refs.bulkHandlingFeeModal).off('hidden.bs.modal');
		}
	},
};
</script>

<style scoped>
#bulk-handling-fee-modal .modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

#bulk-handling-fee-modal .modal-footer {
  border-top: 2px solid #dee2e6;
}

#bulk-handling-fee-modal .input-group-text {
  background-color: #e9ecef;
  border-color: #ced4da;
}

/* Handling Fee Calculation Model Button Style - Same as ProductUpdateModal */
.handling-fee-btn {
  border: 1px solid #dee2e6;
  color: #6c757d;
}

.handling-fee-btn.active-warning {
  background-color: #fff3cd !important;
  border: 2px solid #ffc107 !important;
}

.handling-fee-btn.active-warning .font-weight-bold {
  color: #856404 !important;
}

.handling-fee-btn.active-warning .text-muted {
  color: #856404 !important;
}

.handling-fee-btn:hover {
  background-color: #f8f9fa;
}

.handling-fee-btn.active-warning:hover {
  background-color: #ffe69c !important;
}
</style>
